import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

import type { ServiceHealth, Trend } from '@/types/health';
import { formatRelativeLive } from '@/utils/formatRelative';

// ---------------------------------------------------------------------------
// Sub-helpers
// ---------------------------------------------------------------------------

interface TrendIconProps {
  trend: Trend;
}

function TrendIcon({ trend }: TrendIconProps) {
  switch (trend) {
    case 'up':
      return (
        <TrendingUpIcon
          aria-label="trend: degrading"
          sx={{ color: 'error.main', fontSize: 20 }}
        />
      );
    case 'down':
      return (
        <TrendingDownIcon
          aria-label="trend: improving"
          sx={{ color: 'success.main', fontSize: 20 }}
        />
      );
    case 'flat':
      return (
        <TrendingFlatIcon
          aria-label="trend: stable"
          sx={{ color: 'text.secondary', fontSize: 20 }}
        />
      );
  }
}

/** Returns the MUI sx color token based on error_rate thresholds. */
function errorRateColor(rate: number): string {
  if (rate > 0.02) return 'error.main';
  if (rate > 0.005) return 'warning.main';
  return 'success.main';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ServiceHealthCardProps {
  health: ServiceHealth;
}

/** Round p99 to a sensible precision: integer ms when ≥10ms, 1 decimal below. */
function formatP99Ms(ms: number): string {
  if (ms >= 10) return Math.round(ms).toString();
  return ms.toFixed(1);
}

export function ServiceHealthCard({ health }: ServiceHealthCardProps) {
  const rpmValue = (health.rps * 60).toFixed(1);
  const errorRatePct = (health.error_rate * 100).toFixed(2);

  return (
    <Card
      data-testid={`service-health-card-${health.service}`}
      sx={{
        minHeight: 140,
        transition: (theme) =>
          theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.easeInOut,
          }),
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Top row: service name + trend arrow */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {health.service}
          </Typography>
          <TrendIcon trend={health.trend} />
        </Box>

        {/* Big-number row: p99 */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
          <Typography variant="h4" component="span" sx={{ lineHeight: 1 }}>
            {health.p99_ms !== null ? formatP99Ms(health.p99_ms) : '—'}
          </Typography>
          {health.p99_ms !== null && (
            <Typography variant="caption" color="text.secondary">
              ms
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            p99
          </Typography>
        </Box>

        {/* Mid row: error rate */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: errorRateColor(health.error_rate), fontWeight: 500 }}
          >
            {errorRatePct}% errors
          </Typography>
        </Box>

        {/* Bottom row: RPM + last updated */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {rpmValue} RPM
          </Typography>
          <Chip
            label={formatRelativeLive(health.last_updated)}
            size="small"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
